import PropTypes from 'prop-types';

import { chunk } from 'core/libs/lodash';

import H3 from '../H3';
import { Block, Section } from '../Grid';

import isNotEmptyReactElementContent from 'core/utils/content';
import { resolveScopedStyles } from 'core/utils/styled-jsx';

import styles from './index.styl';


/**
 * @author Alexey Rezakov <a.rezakov@rambler-co.ru>
 */
function Raker(props) {
  const {
    title,
    height,
    content,
    sidebarGridSize,
    interitemSpacing,
    card: Card,
    heroCard: HeroCard,
    headerComponent: Header,
    sidebarColumnCount,
    dataQa,
  } = props;

  if (!content?.length) return null;

  const [firstItem, ...otherItems] = content;
  const sidebarColumns = chunk(otherItems, sidebarColumnCount);

  const scope = resolveScopedStyles(
    <scope>
      <style jsx>{`
        .header
          margin-bottom ${interitemSpacing + 'px'}
        .container
          margin ${-interitemSpacing / 2 + 'px'}
        .hero
          height ${height ? height + 'px' : '100%'}
          padding ${interitemSpacing / 2 + 'px'}
        .list
          height ${height ? height + 'px' : '100%'}
        .item
          margin ${interitemSpacing / 2 + 'px'}
      `}</style>
    </scope>
  );

  return (
    <div {...dataQa && { 'data-qa': dataQa }}>
      {isNotEmptyReactElementContent(title) && (
        <Header
          className={scope.wrapClassNames('header')}
          {...dataQa && { 'data-qa': dataQa + '-title' }}
        >
          {title}
        </Header>
      )}
      <div
        className={scope.wrapClassNames('container')}
        {...dataQa && { 'data-qa': dataQa + '-cards' }}
      >
        <Section>
          <Block mobile={12}>
            <div
              className={scope.wrapClassNames('hero', styles.hero)}
              {...dataQa && { 'data-qa': dataQa + '-hero-card' }}
            >
              {firstItem && (
                <HeroCard content={firstItem} />
              )}
            </div>
          </Block>
          <Block
            width={sidebarGridSize}
            mobile={12}
          >
            <div
              className={scope.wrapClassNames('list', styles.list)}
              {...dataQa && { 'data-qa': dataQa + '-list' }}
            >
              {sidebarColumns.map((items, i) => {
                return (
                  <Section key={i}>
                    {items.map(topic => (
                      <Block key={topic.id} className={scope.wrapClassNames('item')}>
                        <Card content={topic} />
                      </Block>
                    ))}
                  </Section>
                );
              })}
            </div>
          </Block>
        </Section>
      </div>
      <scope.styles />
    </div>
  );
}

Raker.propTypes = {
  /**
   * Компонент для маленьких карточек
   */
  card: PropTypes.func.isRequired,

  /**
   * Данные о топиках.
   */
  content: PropTypes.array,

  /**
   * Компонент, в который будет завёрнут заголовок блока.
   */
  headerComponent: PropTypes.func,

  /**
   * Ограничение блока по высоте на десктопном лейауте.
   * На мобильном высота компонента зависит от высоты карточек.
   */
  height: PropTypes.number,

  /**
   * Компонент главной карточки слева.
   * Карточка должна уметь подстраиваться по высоте контейнера.
   */
  heroCard: PropTypes.func.isRequired,

  /**
   * Ширина колонки с маленькими карточками относительно 12 колоночного грида.
   *
   * Может так же принимать строковое значение с размером на манер `width`
   * у компонента `GridBlock`.
   *
   * При нескольких колонках задает общую ширину всех колонок.
   */
  sidebarGridSize: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  ]),

  /**
   * Заголовок блока.
   */
  title: PropTypes.node,

  /**
   * Расстояние между карточками (горизонтальное + вертикальное).
   */
  interitemSpacing: PropTypes.number.isRequired,

  /**
   * Кол-во колонок с маленькими каточками
   */
  sidebarColumnCount: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]),

  /** @ignore */
  dataQa: PropTypes.string,
};

Raker.defaultProps = {
  sidebarGridSize: 4,
  interitemSpacing: 0,
  headerComponent: H3,
  sidebarColumnCount: 1,
};

export { Raker as StorybookComponent };
export default Raker;
